import { useTranslation } from '@repo/roche-xx-shared';
import { RocheUIDrawer, RocheUIDrawerDirection } from '@rocheglobal/roche-ui';
import React, { CSSProperties, type FC, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { preventDialogScroll } from '../../helpers/prevent-dialog-scroll/index';

interface RocheDrawerProps {
  direction?: RocheUIDrawerDirection;
  fullSize?: boolean;
  fadeOutTrigger?: boolean;
  dialogLabel: string;
  children: ReactNode;
  trigger: ReactNode;
}

export const RocheDrawer: FC<RocheDrawerProps> = ({
  direction = RocheUIDrawerDirection.BOTTOM,
  fullSize = false,
  fadeOutTrigger = false,
  dialogLabel,
  children,
  trigger,
}) => {
  const [opening, setOpening] = useState(false);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const openedWithKeyboard = useRef(false);
  const closeButtonLabel = useTranslation('close');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (opening && dialogRef.current && !dialogRef.current.open) {
      dialogRef.current.showModal();
      if (!openedWithKeyboard.current) {
        (document.activeElement as HTMLElement)?.blur();
      }
      preventDialogScroll(dialogRef.current);
      requestAnimationFrame(() => {
        setOpen(true);
      });
    }
  }, [opening]);

  const handleOpen = useCallback((event: React.MouseEvent | React.KeyboardEvent) => {
    openedWithKeyboard.current = (event as React.KeyboardEvent).detail === 0;
    setOpening(true);
  }, []);

  const handleClose = useCallback(() => {
    (document.activeElement as HTMLElement)?.blur();
    setOpen(false);
    setClosing(true);
  }, []);

  const handleTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDialogElement>) => {
      if (event.target !== dialogRef.current || event.propertyName !== 'transform') return;

      if (closing) {
        setClosing(false);
        setOpening(false);
        dialogRef.current?.close();
      }
    },
    [closing],
  );

  if (!isMounted) {
    return <>{trigger}</>;
  }

  // needed as inline style to fade out trigger if wanted, because it's on the same level as the drawer
  const triggerStyle: CSSProperties = fadeOutTrigger
    ? {
        display: 'inline-block',
        opacity: opening ? 0 : 1,
        pointerEvents: opening ? 'none' : 'auto',
      }
    : {};

  return (
    <>
      <span style={triggerStyle} onClick={handleOpen}>
        {trigger}
      </span>

      {createPortal(
        <RocheUIDrawer
          ref={dialogRef}
          open={open}
          closing={closing}
          direction={direction}
          fullSize={fullSize}
          dialogLabel={dialogLabel}
          closeButtonLabel={closeButtonLabel}
          onClose={handleClose}
          onTransitionEnd={handleTransitionEnd}
        >
          {children}
        </RocheUIDrawer>,
        document.body,
      )}
    </>
  );
};
